import React, { useMemo, useCallback } from 'react';
import { Collapse } from 'antd';

import PropTypes from 'prop-types';
import OnTraccrCheckbox from './inputs/OnTraccrCheckbox';

const { Panel } = Collapse;
function flattenNodes(nodes) {
  return nodes.reduce(
    (acc, n) => acc.concat(n, flattenNodes(n.children || [])),
    [],
  );
}

export default function FilterNode({
  node,
  activeSet,
  onFilterChange,
  category,
  level,
  expandedKeys,
  toggleExpand,
}) {
  const descendantKeys = useMemo(
    () => flattenNodes([node]).map((n) => n.key),
    [node],
  );

  const hasChildren = node.children?.length > 0;

  const selectedCount = descendantKeys.filter((k) => activeSet.has(k)).length;
  const checked = (
    (!hasChildren && activeSet.has(node.key))
    || (hasChildren && selectedCount === (descendantKeys.length - 1))
  );

  const indeterminate = hasChildren
   && selectedCount > 0
   && selectedCount < (descendantKeys.length - 1);

  const handleChange = useCallback(
    (nextChecked) => {
      const next = new Set(activeSet);
      if (node.children?.length > 0) {
        if (nextChecked) descendantKeys.forEach((k) => next.add(k));
        else descendantKeys.forEach((k) => next.delete(k));
      } else if (nextChecked) {
        next.add(node.key);
      } else {
        next.delete(node.key);
      }
      onFilterChange(Array.from(next), category);
    },
    [activeSet, descendantKeys, node, onFilterChange, category],
  );

  const isExpanded = expandedKeys.has(node.key);

  const onCollapseChange = () => toggleExpand(node.key);

  if (hasChildren) {
    return (
      <div className="filter-node-override">
        <Collapse
          activeKey={isExpanded ? [node.key] : []}
          onChange={onCollapseChange}
          ghost
          expandIconPosition="start"
          style={{
            marginLeft: level * 60,
            marginBottom: 8,
            transition: 'all 0.2s ease',
          }}
        >
          <Panel
            collapsible="icon"
            header={(
              <div role="button" tabIndex={0} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
                <OnTraccrCheckbox
                  label={node.title}
                  value={checked}
                  onChange={handleChange}
                  indeterminate={indeterminate}
                />
              </div>
            )}
            key={node.key}
            showArrow
          >
            {node.children.map((child) => (
              <FilterNode
                key={child.key}
                node={child}
                activeSet={activeSet}
                onFilterChange={onFilterChange}
                category={category}
                level={level + 1}
                expandedKeys={expandedKeys}
                toggleExpand={toggleExpand}
              />
            ))}
          </Panel>
        </Collapse>
      </div>
    );
  }

  return (
    <div
      style={{
        marginLeft: level * 60,
        marginBottom: 8,
        transition: 'all 0.2s ease',
      }}
    >
      <OnTraccrCheckbox
        label={node.title}
        value={checked}
        onChange={handleChange}
      />
    </div>
  );
}

FilterNode.propTypes = {
  node: PropTypes.shape({
    title: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
    children: PropTypes.array,
  }).isRequired,
  activeSet: PropTypes.instanceOf(Set).isRequired,
  onFilterChange: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired,
  level: PropTypes.number.isRequired,
  expandedKeys: PropTypes.instanceOf(Set).isRequired,
  toggleExpand: PropTypes.func.isRequired,
};
